<template>
  <div
    class="appointment-report ar-success"
    :class="{ 'ar-success--compact': compact }"
  >
    <div class="ar-success__hero">
      <svg
        v-if="compact"
        class="ar-success__mark"
        width="56"
        height="56"
        viewBox="0 0 52 52"
        fill="none"
        aria-hidden="true"
      >
        <circle
          class="ar-success__mark-circle ar-success__mark-circle--compact"
          cx="26"
          cy="26"
          r="24"
          stroke-width="3"
          transform="rotate(-90 26 26)"
        />
        <polyline
          class="ar-success__mark-check ar-success__mark-check--compact"
          points="15 27 23 34 37 19"
          stroke-width="3"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <svg
        v-else
        class="ar-success__mark"
        width="64"
        height="64"
        viewBox="0 0 64 64"
        fill="none"
        aria-hidden="true"
      >
        <circle
          class="ar-success__mark-fill"
          cx="32"
          cy="32"
          r="28"
        />
        <circle
          class="ar-success__mark-circle"
          cx="32"
          cy="32"
          r="28"
          stroke-width="3"
          stroke-linecap="round"
          transform="rotate(-90 32 32)"
        />
        <polyline
          class="ar-success__mark-check"
          points="21 33 29 41 44 25"
          stroke-width="3"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>

      <component
        :is="resolvedHeadingTag"
        ref="heading"
        tabindex="-1"
        class="ar-success__title ar-focus-target ar-anim-rise"
        style="--ar-delay: 300ms"
      >
        {{ title || FIGMA_COPY.savedTitle }}
      </component>
      <p
        v-if="resolvedCaption"
        class="ar-success__caption ar-tabular ar-anim-rise"
        style="--ar-delay: 360ms"
      >
        {{ resolvedCaption }}
      </p>
    </div>

    <p
      v-if="notice"
      class="ar-callout ar-success__notice ar-anim-rise"
      :class="`ar-callout--${noticeVariant}`"
      style="--ar-delay: 380ms"
    >
      <feather-icon
        icon="InfoIcon"
        size="16"
        aria-hidden="true"
      />
      <span>{{ notice }}</span>
    </p>

    <section
      v-if="lines.length || appointment"
      class="ar-success__summary ar-anim-rise"
      :aria-labelledby="summaryTitleId"
      style="--ar-delay: 400ms"
    >
      <div class="ar-success__summary-head">
        <component
          :is="summaryHeadingTag"
          :id="summaryTitleId"
          class="ar-success__summary-title"
        >
          {{ NEW_COPY.summaryTitle }}
        </component>
        <span
          v-if="customerName"
          class="ar-success__customer"
        >
          <span
            class="ar-avatar ar-avatar--sm"
            aria-hidden="true"
          >{{ customerInitials }}</span>
          {{ customerName }}
        </span>
      </div>
      <dl class="ar-success__lines">
        <div
          v-for="(line, index) in lines"
          :key="line.key"
          class="ar-success__line ar-anim-crossfade"
          :class="{ 'ar-success__line--sub': line.sub, 'ar-success__line--block': line.block }"
          :style="{ '--ar-delay': `${440 + Math.min(index, 3) * 40}ms` }"
        >
          <dt class="ar-success__line-label">
            {{ line.label }}
          </dt>
          <dd class="ar-success__line-value">
            {{ line.value }}
          </dd>
        </div>
      </dl>
      <transition name="ar-reveal">
        <div
          v-if="detailsOpen && details.length"
          class="ar-success__details-wrap"
        >
          <dl
            :id="detailsId"
            class="ar-success__details"
          >
            <div
              v-for="field in details"
              :key="field.key"
              class="ar-success__detail"
            >
              <dt class="ar-success__detail-label">
                {{ field.label }}
              </dt>
              <dd
                class="ar-success__detail-value"
                :class="{ 'ar-tabular': field.tabular }"
              >{{ field.value }}</dd>
            </div>
          </dl>
        </div>
      </transition>
    </section>

    <div
      v-if="$scopedSlots.next || $slots.next"
      class="ar-success__next ar-anim-rise"
      style="--ar-delay: 560ms"
    >
      <slot
        name="next"
        :toggle-details="toggleDetails"
        :details-open="detailsOpen"
        :details-id="detailsOpen ? detailsId : null"
        :has-details="details.length > 0"
        :details-label="APP_COPY.reportDetails"
      />
    </div>
  </div>
</template>

<script>
import {
  APPOINTMENT_FIELD_LABELS,
  APP_COPY,
  FIGMA_COPY,
  NEW_COPY,
  NOT_HELD_REASON,
  REASON_SELECTS,
  reportSummaryLines,
} from '@/constants/appointment_report_v2'
import { X_MOMENT } from '@/constants/appointment_types'
import {
  answersFromReport,
  appointmentEndForDisplay,
  formatDateTime,
  formatShortDate,
  formatTime,
  initialsOf,
  nextReportUid,
} from '../helpers/report_ui'

const EMPTY_VALUE = '—'

/**
 * Confirmação real do report ("MX Salvo"): marca de sucesso desenhada (círculo 360ms + check 240ms),
 * título, "Finalizado por Judit Polgar em 23/02/26 às 14:07", o "Resumo do report" (cada pergunta respondida
 * do caminho ativo + o motivo escolhido, entrando com 40ms de intervalo) e o slot `next` para os próximos passos.
 * Sem confete, sem redirecionamento automático. O título recebe o foco ao montar (`autofocus`).
 *
 * Fontes do resumo (a primeira que vier): `summary` (linhas prontas `{ key, label, value, sub?, block? }` — `block`
 * põe o valor embaixo do rótulo, para texto livre como os comentários), `answers`
 * (respostas do formulário) ou `report` (resumo do servidor — `presentReport`, também preenche quem/quando).
 *
 * Slot `next` (escopo): `{ toggleDetails, detailsOpen, detailsId, hasDetails, detailsLabel }` — para o botão
 * "Detalhes do report" (`aria-expanded="detailsOpen"`, `aria-controls="detailsId"`), que revela os 6 campos
 * do compromisso (`appointment`) dentro do cartão do resumo.
 *
 * `compact` = confirmação do painel da central ("Report salvo", marca de 56px, resumo em caixa).
 */
export default {
  name: 'ReportSuccess',
  props: {
    title: {
      type: String,
      default: '',
    },
    // Substitui a linha "Finalizado por … em … às …" (ex.: "Emir Salin · Momento X · 22/02/26 | 13:00").
    caption: {
      type: String,
      default: '',
    },
    reportedBy: {
      type: String,
      default: '',
    },
    reportedAt: {
      type: String,
      default: '',
    },
    summary: {
      type: Array,
      default: null,
    },
    answers: {
      type: Object,
      default: null,
    },
    report: {
      type: Object,
      default: null,
    },
    customerName: {
      type: String,
      default: '',
    },
    appointment: {
      type: Object,
      default: null,
    },
    notice: {
      type: String,
      default: '',
    },
    noticeVariant: {
      type: String,
      default: 'info',
      validator: value => ['info', 'warning', 'success'].includes(value),
    },
    compact: {
      type: Boolean,
      default: false,
    },
    headingTag: {
      type: String,
      default: '',
    },
    autofocus: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      APP_COPY,
      FIGMA_COPY,
      NEW_COPY,
      uid: nextReportUid('ar-success'),
      detailsOpen: false,
    }
  },
  computed: {
    resolvedHeadingTag() {
      return this.headingTag || (this.compact ? 'h3' : 'h2')
    },
    summaryHeadingTag() {
      return this.resolvedHeadingTag === 'h2' ? 'h3' : 'h4'
    },
    summaryTitleId() {
      return `${this.uid}-summary`
    },
    detailsId() {
      return `${this.uid}-details`
    },
    resolvedReportedBy() {
      if (this.reportedBy) return this.reportedBy
      const person = this.report && this.report.reported_by
      return person && person.name ? String(person.name) : ''
    },
    resolvedReportedAt() {
      return this.reportedAt || (this.report && this.report.created_at) || ''
    },
    resolvedCaption() {
      if (this.caption) return this.caption
      if (!this.resolvedReportedBy || !this.resolvedReportedAt) return ''
      return NEW_COPY.finalizedBy(
        this.resolvedReportedBy,
        formatShortDate(this.resolvedReportedAt),
        formatTime(this.resolvedReportedAt),
      )
    },
    lines() {
      let lines = []
      if (Array.isArray(this.summary)) lines = this.summary
      else if (this.answers) lines = reportSummaryLines(this.answers)
      else if (this.report) lines = this.reportLines(this.report)

      return lines
        .filter(line => line && line.value)
        .map(line => ({ ...line, sub: line.sub !== undefined ? !!line.sub : !!REASON_SELECTS[line.key] }))
    },
    customerInitials() {
      return initialsOf(this.customerName)
    },
    details() {
      const source = this.appointment
      if (!source) return []
      const name = entity => (entity && entity.name ? String(entity.name) : EMPTY_VALUE)
      const eventType = source.event_type_description
        || (Number(source.event_type) === X_MOMENT ? APP_COPY.momentX : EMPTY_VALUE)

      return [
        { key: 'customer', value: name(source.customer) },
        { key: 'consultant', value: name(source.consultant) },
        { key: 'specialist', value: name(source.consultant_specialist) },
        { key: 'eventType', value: eventType },
        { key: 'start', value: formatDateTime(source.appointment_start) || EMPTY_VALUE, tabular: true },
        {
          key: 'end',
          value: formatDateTime(appointmentEndForDisplay(source.appointment_end)) || EMPTY_VALUE,
          tabular: true,
        },
      ].map(field => ({ ...field, label: APPOINTMENT_FIELD_LABELS[field.key] }))
    },
  },
  mounted() {
    if (this.autofocus) this.$nextTick(() => this.focus())
  },
  methods: {
    focus() {
      if (this.$refs.heading) this.$refs.heading.focus({ preventScroll: false })
    },
    toggleDetails() {
      this.detailsOpen = !this.detailsOpen
    },
    // Report do servidor → linhas. Report legado (report_version 1, sidebar antiga) de reunião não realizada
    // não tem `not_held_reason`: o motivo gravado em `why` (texto livre das opções antigas) entra no lugar.
    reportLines(report) {
      const lines = reportSummaryLines(answersFromReport(report))
      const legacyWhy = typeof report.why === 'string' ? report.why.trim() : ''
      const notHeldKey = REASON_SELECTS[NOT_HELD_REASON].key
      if (report.had_meeting === false && legacyWhy && !lines.some(line => line.key === notHeldKey)) {
        lines.push({
          key: notHeldKey,
          label: REASON_SELECTS[NOT_HELD_REASON].label,
          value: legacyWhy,
        })
      }

      return lines
    },
  },
}
</script>

<style lang="scss" src="@/assets/scss/_appointment-report.scss"></style>

<style lang="scss" scoped>
.ar-success {
  width: 100%;
  max-width: 640px;
  margin: 12px auto 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.ar-success__hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
}

.ar-success__mark {
  margin-bottom: 8px;
}

.ar-success__mark-fill {
  fill: var(--ar-success-mark-fill);
}

.ar-success__mark-circle {
  stroke: var(--ar-success);
  stroke-dasharray: 176;
  stroke-dashoffset: 176;
  animation: ar-draw 360ms var(--ar-ease-draw) forwards;
}

.ar-success__mark-circle--compact {
  stroke-dasharray: 152;
  stroke-dashoffset: 152;
  animation-timing-function: var(--ar-ease-standard);
}

.ar-success__mark-check {
  stroke: var(--ar-success-text);
  stroke-dasharray: 36;
  stroke-dashoffset: 36;
  animation: ar-draw 240ms 240ms var(--ar-ease-draw) forwards;
}

.ar-success__mark-check--compact {
  stroke-dasharray: 34;
  stroke-dashoffset: 34;
  animation-timing-function: var(--ar-ease-standard);
}

.ar-success__title {
  margin: 0;
  font-size: 24px;
  line-height: 32px;
  font-weight: 600;
  letter-spacing: -.2px;
  color: var(--ar-text-1);
}

.ar-success__caption {
  margin: 0;
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-2);
}

.ar-success__notice {
  width: 100%;
}

// ---- Resumo ----
.ar-success__summary {
  width: 100%;
  padding: 20px 24px 8px;
  border-radius: var(--ar-radius-card);
  background: var(--ar-bg-card);
  box-shadow: var(--ar-shadow-e1);
}

.ar-success__summary-head {
  padding-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.ar-success__summary-title {
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: var(--ar-text-1);
}

.ar-success__customer {
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-2);
}

.ar-success__lines {
  margin: 0;
}

.ar-success__line {
  padding: 12px 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  border-top: 1px solid var(--ar-divider);
}

// Motivo: recuado com a linha de ramificação (mesma do formulário).
.ar-success__line--sub {
  padding-left: 16px;
  box-shadow: inset 2px 0 0 var(--ar-branch);
}

.ar-success__line-label {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: var(--ar-text-2);
}

.ar-success__line-value {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 55%;
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-1);
  text-align: right;
  // Texto digitado pelo PN pode ter palavras sem espaço (URL, "aaaa…"): quebra em vez de vazar do cartão.
  overflow-wrap: anywhere;
  word-break: break-word;
}

// Texto livre (comentários): rótulo em cima e o texto embaixo, à esquerda, com as quebras de linha do PN.
.ar-success__line--block {
  flex-direction: column;
  align-items: stretch;
  gap: 4px;

  .ar-success__line-value {
    width: 100%;
    max-width: none;
    font-weight: 400;
    text-align: left;
    white-space: pre-line;
  }
}

.ar-success__details {
  margin: 4px 0 0;
  padding: 16px 0 12px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 20px;
  row-gap: 16px;
  border-top: 1px solid var(--ar-divider);
}

.ar-success__detail {
  min-width: 0;
}

.ar-success__detail-label {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  color: var(--ar-text-label);
}

.ar-success__detail-value {
  margin: 4px 0 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--ar-text-1);
  white-space: pre;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ar-success__next {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

// ---- Compacto (painel da central) ----
.ar-success--compact {
  max-width: none;
  margin: auto 0;
  padding: 24px 0;
  gap: 20px;

  .ar-success__hero {
    gap: 4px;
  }

  .ar-success__mark {
    margin-bottom: 12px;
  }

  .ar-success__title {
    font-size: 20px;
    line-height: 28px;
    letter-spacing: 0;
  }

  .ar-success__summary {
    max-width: 520px;
    padding: 4px 16px;
    border: 1px solid var(--ar-divider);
    background: var(--ar-bg-field);
    box-shadow: none;
  }

  .ar-success__summary-head {
    display: none;
  }

  .ar-success__line {
    padding: 10px 0;
    align-items: baseline;
    gap: 16px;

    &:first-child {
      border-top: 0;
    }
  }

  .ar-success__line-label {
    font-size: 13px;
    font-weight: 500;
  }

  .ar-success__line--block {
    align-items: stretch;
    gap: 4px;
  }
}

@media (max-width: 575.98px) {
  .ar-success__summary {
    padding: 16px 16px 8px;
  }

  .ar-success__line {
    flex-direction: column;
    gap: 4px;
  }

  .ar-success__line-value {
    max-width: none;
    text-align: left;
  }

  .ar-success__details {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
